import { z } from 'zod';

/**
 * @file maternity-phase.schema.ts
 * @description As seis fases da maternidade — o vocabulário canônico de jornada.
 *
 * Este enum é o CONCEITO: o que a mulher vê e escolhe no app. Não confundir com
 * `UserTypeEnum` (user.schema.ts), que é o ARMAZENAMENTO: lá `PREGNANT` cobre
 * gestante e puérpera, distinguidas por `pregnancy.status`, e existe
 * `FATHER_FIGURE`, que não é fase de maternidade.
 *
 * O caminho completo é: a mulher escolhe uma fase → grava-se `userType` mais o
 * estado da gestação → `resolveMaternityPhase` reconstrói a fase a partir disso.
 *
 * Morava em `partner.schema.ts` por acidente histórico — o primeiro consumidor
 * foi o Page Builder. Não tem relação com parceiros.
 */
export const MaternityPhaseEnum = z.enum([
  'TENTANTE',
  'GESTANTE',
  'PUERPERA',
  'MAE_DE_ANJO',
  'ADOTANTE',
  'MAE',
]);

export type MaternityPhase = z.infer<typeof MaternityPhaseEnum>;

/**
 * Fases que vêm direto do `userType`, sem relação com gestação. Diferente de
 * gestante, puérpera e perdi meu bebê, elas não evoluem no tempo.
 */
export const STATIC_MATERNITY_PHASES = [
  'TENTANTE',
  'MAE_DE_ANJO',
  'ADOTANTE',
  'MAE',
] as const;

/**
 * Duração do puerpério, em dias. Passado esse ponto a mulher é MAE.
 *
 * Seis semanas, a convenção clínica usual. A transição é DERIVADA da data do
 * parto de propósito: marcá-la à mão exigiria alguém lembrar de reclassificar
 * cada puérpera, e esquecer é o padrão.
 */
export const PUERPERIO_DIAS = 42;

/** O que basta saber da gestação para derivar a fase. */
export interface PhaseInputPregnancy {
  status?: 'active' | 'completed' | 'interrupted' | null;
  childbirthDate?: Date | string | ComToDate | null;
}

export interface PhaseInput {
  userType?: string | null;
  pregnancy?: PhaseInputPregnancy | null;
}

export interface MaternityPhaseResult {
  phase: MaternityPhase;
  /**
   * Dias desde o parto, quando aplicável. `null` quando a gestação está
   * concluída mas ninguém informou a data — caso em que a fase permanece
   * PUERPERA e a tela deve dizer "data não informada" em vez de supor.
   */
  diasPosParto: number | null;
}

/** Algo que se comporta como Timestamp do Firestore, sem importar o SDK aqui. */
interface ComToDate {
  toDate(): Date;
}

function temToDate(v: unknown): v is ComToDate {
  return typeof v === 'object' && v !== null && typeof (v as ComToDate).toDate === 'function';
}

function paraData(v: Date | string | ComToDate | null | undefined): Date | null {
  if (!v) return null;
  // Timestamp do Firestore NÃO é instanceof Date, e `new Date(timestamp)`
  // devolve Invalid Date. Sem este ramo, todo registro gravado como Timestamp
  // cairia em "data não informada" e a puérpera nunca viraria mãe — falha
  // silenciosa, sem erro nenhum.
  if (temToDate(v)) {
    const d = v.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Deriva a fase da maternidade a partir do que está gravado.
 *
 * Regra única e compartilhada — antes existiam três implementações
 * (`resolveJourney` no admin, `derivePhaseLabel` e `derivePhaseFromUser` no
 * mobile), cada uma com um buraco diferente: uma ignorava gestação
 * interrompida e as outras duas devolviam GESTANTE para quem tinha
 * `userType: 'MAE'`.
 *
 * `agora` é injetável para o cálculo ser testável sem depender do relógio.
 */
export function resolveMaternityPhase(
  user: PhaseInput,
  agora: Date = new Date(),
): MaternityPhaseResult {
  const semPosParto = (phase: MaternityPhase): MaternityPhaseResult => ({
    phase,
    diasPosParto: null,
  });

  // Fases estáticas ganham do estado da gestação: quem se declarou tentante ou
  // adotante é isso, mesmo que exista uma gestação antiga no cadastro.
  if (
    user.userType &&
    (STATIC_MATERNITY_PHASES as readonly string[]).includes(user.userType)
  ) {
    return semPosParto(user.userType as MaternityPhase);
  }

  const gestacao = user.pregnancy ?? null;
  if (!gestacao) return semPosParto('GESTANTE');

  // Perda gestacional: o mobile já derivava daqui, o admin não — e exibia
  // essas mulheres como gestantes.
  if (gestacao.status === 'interrupted') return semPosParto('MAE_DE_ANJO');

  if (gestacao.status === 'completed') {
    const parto = paraData(gestacao.childbirthDate);
    // Sem data não dá para saber se o puerpério acabou. Fica PUERPERA e o
    // `diasPosParto: null` sinaliza para a tela dizer "data não informada" —
    // supor "mãe" seria inventar.
    if (!parto) return semPosParto('PUERPERA');

    const dias = Math.floor((agora.getTime() - parto.getTime()) / 86_400_000);
    return {
      phase: dias > PUERPERIO_DIAS ? 'MAE' : 'PUERPERA',
      diasPosParto: dias,
    };
  }

  return semPosParto('GESTANTE');
}
